// Mock data for About and Integrated Healthcare pages

export const aboutData = {
  story: {
    title: "Our Story",
    subtitle: "Bridging Ancient Wisdom with Modern Innovation",
    content: "Founded with a vision to revolutionize healthcare delivery, Ayurmed Global emerged from the understanding that the future of medicine lies in the harmonious integration of time-tested Ayurvedic principles and cutting-edge artificial intelligence. Our journey began when a team of healthcare visionaries, Ayurvedic practitioners, and technology innovators came together to address the growing need for holistic, personalized, and technologically advanced healthcare solutions.",
    yearFounded: "2018",
    countriesServed: "25+",
    hospitalsIntegrated: "150+",
    teamMembers: "200+"
  },
  mission: {
    title: "Our Mission",
    content: "To democratize access to integrated Ayurvedic healthcare by empowering hospitals, educational institutions, and healthcare providers with AI-driven solutions that honor traditional healing wisdom while embracing modern medical technology."
  },
  vision: {
    title: "Our Vision",
    content: "To become the global leader in AI-powered Ayurvedic healthcare integration, creating a world where ancient healing wisdom and modern technology work in perfect harmony to deliver personalized, effective, and compassionate care to every patient."
  },
  values: [
    {
      id: 1,
      title: "Innovation with Tradition",
      description: "We honor 5000 years of Ayurvedic wisdom while pioneering AI-powered healthcare solutions.",
      icon: "lightbulb"
    },
    {
      id: 2,
      title: "Patient-Centric Care",
      description: "Every solution we create puts patient wellbeing, dignity, and personalized care at the center.",
      icon: "heart"
    },
    {
      id: 3,
      title: "Excellence & Quality",
      description: "We maintain the highest standards in technology, medical practice, and service delivery.",
      icon: "award"
    },
    {
      id: 4,
      title: "Global Collaboration",
      description: "We build bridges between traditional practitioners, modern healthcare, and technology experts worldwide.",
      icon: "globe"
    },
    {
      id: 5,
      title: "Continuous Learning",
      description: "We invest in research, education, and development to stay at the forefront of integrated healthcare.",
      icon: "book-open"
    },
    {
      id: 6,
      title: "Sustainable Impact",
      description: "We create solutions that are environmentally conscious, economically viable, and socially responsible.",
      icon: "leaf"
    }
  ],
  coreValues: {
    title: "Our Core Values",
    subtitle: "The principles that guide everything we do",
    values: [
      {
        id: 1,
        title: "Innovation with Tradition",
        description: "We honor 5000 years of Ayurvedic wisdom while pioneering AI-powered healthcare solutions.",
        icon: "lightbulb"
      },
      {
        id: 2,
        title: "Patient-Centric Care",
        description: "Every solution we create puts patient wellbeing, dignity, and personalized care at the center.",
        icon: "heart"
      },
      {
        id: 3,
        title: "Excellence & Quality",
        description: "We maintain the highest standards in technology, medical practice, and service delivery.",
        icon: "award"
      },
      {
        id: 4,
        title: "Global Collaboration",
        description: "We build bridges between traditional practitioners, modern healthcare, and technology experts worldwide.",
        icon: "globe"
      },
      {
        id: 5,
        title: "Continuous Learning",
        description: "We invest in research, education, and development to stay at the forefront of integrated healthcare.",
        icon: "users"
      },
      {
        id: 6,
        title: "Sustainable Impact",
        description: "We create solutions that are environmentally conscious, economically viable, and socially responsible.",
        icon: "leaf"
      }
    ]
  },
  team: [
    {
      id: 1,
      name: "Dr. Rajesh Kumar",
      role: "Founder & CEO",
      expertise: "Ayurvedic Medicine & Healthcare Technology",
      description: "25+ years in Ayurvedic practice with deep expertise in healthcare innovation."
    },
    {
      id: 2,
      name: "Sarah Chen",
      role: "Chief Technology Officer",
      expertise: "AI & Machine Learning",
      description: "Former AI lead at major tech companies, pioneering healthcare AI solutions."
    },
    {
      id: 3,
      name: "Dr. Priya Sharma",
      role: "Chief Medical Officer",
      expertise: "Integrated Medicine & Clinical Operations",
      description: "Board-certified physician specializing in integrated Ayurvedic care."
    },
    {
      id: 4,
      name: "Mohammed Al-Rashid",
      role: "VP of International Operations",
      expertise: "Global Healthcare Strategy",
      description: "Led healthcare integration projects across Middle East and Asia."
    }
  ]
};

export const integratedHealthcareData = {
  hero: {
    title: "Integrated Healthcare Approach",
    subtitle: "Where Ancient Ayurvedic Wisdom Meets Modern AI Technology",
    description: "Our integrated healthcare model seamlessly combines the holistic principles of Ayurveda with state-of-the-art artificial intelligence, creating a comprehensive care system that addresses the physical, mental, and spiritual aspects of health."
  },
  definition: {
    title: "What is Integrated Healthcare?",
    content: "Integrated healthcare is a patient-centered approach that combines conventional Western medicine with traditional healing systems like Ayurveda. By leveraging the strengths of both systems, we provide comprehensive care that addresses not just symptoms, but the root causes of health issues.",
    keyPoints: [
      "Combines Ayurvedic principles with modern diagnostics",
      "AI-powered personalized treatment recommendations",
      "Holistic approach addressing mind, body, and spirit",
      "Evidence-based integration of traditional and modern therapies",
      "Continuous monitoring and adaptive care plans"
    ]
  },
  approach: [
    {
      id: 1,
      title: "Holistic Patient Assessment",
      description: "AI-powered systems analyze patient constitution (Prakriti) combined with modern diagnostic data to create comprehensive health profiles.",
      icon: "user-search",
      benefits: [
        "Personalized treatment plans",
        "Constitutional analysis",
        "Predictive health insights",
        "Preventive care strategies"
      ]
    },
    {
      id: 2,
      title: "Traditional + Modern Diagnostics",
      description: "Integration of Ayurvedic assessment methods (Nadi Pariksha, Tongue diagnosis) with advanced medical diagnostics and AI analysis.",
      icon: "activity",
      benefits: [
        "Comprehensive diagnosis",
        "Early disease detection",
        "Root cause identification",
        "Multi-dimensional health view"
      ]
    },
    {
      id: 3,
      title: "Personalized Treatment Protocols",
      description: "AI algorithms recommend optimal combinations of Ayurvedic therapies, herbal formulations, and lifestyle modifications.",
      icon: "clipboard-list",
      benefits: [
        "Tailored medicine selection",
        "Customized therapy plans",
        "Lifestyle recommendations",
        "Dietary guidance"
      ]
    },
    {
      id: 4,
      title: "Continuous Monitoring & Adaptation",
      description: "Real-time tracking of patient progress with AI-driven insights enabling dynamic treatment adjustments.",
      icon: "trending-up",
      benefits: [
        "Progress tracking",
        "Treatment optimization",
        "Outcome prediction",
        "Adaptive care plans"
      ]
    },
    {
      id: 5,
      title: "Cultural & Local Intelligence",
      description: "Deep understanding of regional health practices, local herbs, and cultural dietary patterns integrated into treatment protocols.",
      icon: "globe",
      benefits: [
        "Region-specific remedies",
        "Local herb utilization",
        "Cultural dietary alignment",
        "Community health insights"
      ]
    },
    {
      id: 6,
      title: "Personalized Lifestyle Coaching",
      description: "One-on-one guidance combining Ayurvedic daily routines (Dinacharya), nutrition plans, yoga practices, and stress management.",
      icon: "heart",
      benefits: [
        "Daily routine optimization",
        "Personalized yoga plans",
        "Stress management techniques",
        "Nutritional counseling"
      ]
    }
  ],
  benefits: [
    {
      id: 1,
      title: "Enhanced Patient Outcomes",
      description: "Combining Ayurvedic holistic care with AI precision leads to better treatment results and faster recovery.",
      stat: "40%",
      statDescription: "improvement in patient satisfaction",
      icon: "trending-up"
    },
    {
      id: 2,
      title: "Reduced Treatment Costs",
      description: "Preventive care and personalized treatments reduce unnecessary procedures and medications.",
      stat: "30%",
      statDescription: "reduction in overall treatment costs",
      icon: "dollar-sign"
    },
    {
      id: 3,
      title: "Faster Diagnosis",
      description: "AI-powered systems analyze complex patterns instantly, reducing diagnosis time significantly.",
      stat: "60%",
      statDescription: "faster diagnostic processing",
      icon: "zap"
    },
    {
      id: 4,
      title: "Preventive Care Excellence",
      description: "Early detection and constitutional balancing prevent diseases before they manifest.",
      stat: "50%",
      statDescription: "reduction in chronic disease progression",
      icon: "shield"
    }
  ],
  technologyIntegration: {
    title: "Technology Integration Framework",
    description: "Our multi-layered technology stack ensures seamless integration of AI capabilities with traditional Ayurvedic practice.",
    layers: [
      {
        id: 1,
        name: "Data Collection Layer",
        description: "IoT devices, wearables, and traditional assessment tools capture comprehensive patient data.",
        technologies: ["IoT Sensors", "Wearable Devices", "Digital Pulse Analysis", "Image Recognition"]
      },
      {
        id: 2,
        name: "AI Analysis Layer",
        description: "Machine learning algorithms process data to identify patterns, predict outcomes, and recommend treatments.",
        technologies: ["Deep Learning", "Natural Language Processing", "Predictive Analytics", "Pattern Recognition"]
      },
      {
        id: 3,
        name: "Clinical Decision Support",
        description: "AI-powered recommendations assist practitioners in creating optimal treatment protocols.",
        technologies: ["Expert Systems", "Clinical Algorithms", "Treatment Optimization", "Drug Interaction Analysis"]
      },
      {
        id: 4,
        name: "Patient Engagement Layer",
        description: "Multilingual interfaces enable patients to actively participate in their healing journey.",
        technologies: ["Mobile Apps", "Chatbots", "Telemedicine", "Progress Tracking"]
      }
    ]
  },
  successStories: [
    {
      id: 1,
      hospital: "Dubai Ayurvedic Medical Center",
      country: "UAE",
      challenge: "Integration of Ayurvedic services in modern hospital setting",
      solution: "Complete RHID™ implementation with AI-powered patient management",
      results: [
        "300% increase in patient capacity",
        "95% patient satisfaction rate",
        "40% reduction in administrative overhead",
        "International accreditation achieved"
      ]
    },
    {
      id: 2,
      hospital: "National Ayurveda Institute",
      country: "India",
      challenge: "Modernizing traditional Ayurvedic education and practice",
      solution: "AI-powered education platform and clinical decision support system",
      results: [
        "500+ doctors trained annually",
        "Improved diagnostic accuracy by 45%",
        "Research output increased 3x",
        "Government recognition achieved"
      ]
    },
    {
      id: 3,
      hospital: "Berlin Integrated Wellness Hospital",
      country: "Germany",
      challenge: "Creating Europe's first fully integrated Ayurvedic facility",
      solution: "Turnkey integration including equipment, staff training, and RHID™ software",
      results: [
        "First EU-compliant Ayurvedic hospital",
        "85% treatment success rate",
        "Waitlist of 6 months within first year",
        "Model for 5 additional centers"
      ]
    }
  ]
};
